import { Avatar } from '@pickleballinc/ui/components/ui/avatar';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import clsx from 'clsx';

import ImageWithFallback from '@/components/ImageWithFallback/ImageWithFallback';
import { RankChange } from '@/modules/rankings/components/RankChange';
import { HiddenAge } from '@/modules/rankings/PlayerCard/HiddenAge';
import { formatPoints } from '@/modules/rankings/rankingsUtils';

interface SmallLiveProps {
  rank: string;
  age: string;
  fullName: string;
  country: string;
  avatarUrl: string;
  flagUrl: string;
  livePoints: number;
  currentTournamentUrl?: string;
  currentTournamentText?: string;
  officialPoints: number;
  plusMinus?: number;
  nextPoints?: number;
  maxPoints?: number;
  change: number;
  playerRedirectURL: string;
}

export const SmallLive = ({
  rank,
  age,
  fullName,
  country,
  avatarUrl,
  flagUrl,
  livePoints,
  currentTournamentUrl,
  currentTournamentText,
  officialPoints,
  plusMinus,
  nextPoints,
  maxPoints,
  change,
  playerRedirectURL,
}: SmallLiveProps) => {
  return (
    <div
      className={clsx(
        'container ml-0 flex w-full items-center border-b border-gray-200 p-0',
        currentTournamentUrl ? 'bg-brand-25' : 'bg-white'
      )}
    >
      <div className='flex w-[10%] items-center justify-between pr-2 text-sm font-semibold'>
        <span className='pl-3'>{rank}</span>
        <RankChange change={change} />
      </div>
      <CustomLink
        href={playerRedirectURL}
        className='flex w-[23%] items-center justify-start space-x-3 overflow-hidden rounded-full'
      >
        <Avatar name={fullName} src={avatarUrl || undefined} size='xs' variant='gray' />
        <span className='flex flex-row gap-1'>
          <p className='text-sm font-semibold text-gray-900'>{fullName}</p>
        </span>
      </CustomLink>
      <div className='flex w-1/12 items-center justify-start space-x-3'>
        <ImageWithFallback
          src={flagUrl ? flagUrl : undefined}
          className='fill size-[18px] rounded-full'
          alt={`${country} flag`}
          width={18}
          height={18}
        />
        <span
          className={`text-sm font-semibold ${currentTournamentUrl ? 'text-gray-500' : 'text-gray-900'}`}
        >
          {country}
        </span>
      </div>
      <div
        className={`flex w-1/12 items-center justify-center text-sm ${currentTournamentUrl ? 'text-gray-500' : 'text-gray-900'}`}
      >
        {age === '-1' ? <HiddenAge /> : age}
      </div>
      <div className='flex w-2/12 items-center justify-center rounded-md border border-gray-200 text-xs text-gray-900'>
        {currentTournamentText ? (
          <span className='overflow-hidden text-ellipsis pl-1 text-xs font-medium'>
            {currentTournamentText}
          </span>
        ) : (
          <span className='text-xs'>--</span>
        )}
      </div>
      <div className='flex w-[10%] items-center justify-center text-sm font-semibold text-brand-700'>
        {formatPoints(livePoints)}
      </div>
      <div
        className={`flex w-[10%] items-center justify-center text-sm ${currentTournamentUrl ? 'text-gray-500' : 'text-gray-900'}`}
      >
        {formatPoints(officialPoints)}
      </div>
      {plusMinus ? (
        <div
          className={clsx('flex w-[6%] items-center justify-center text-base font-semibold', {
            'text-gray-900': !currentTournamentText,
            'text-success-500': currentTournamentText && plusMinus > 0,
            'text-error-500': currentTournamentText && plusMinus < 0,
          })}
        >
          {formatPoints(plusMinus)}
        </div>
      ) : (
        <div
          className={`flex w-[6%] items-center justify-center text-base font-semibold text-gray-900`}
        >
          --
        </div>
      )}
      <div className='flex w-[10%] flex-col items-center justify-center text-sm text-gray-900'>
        <div className='text-sm font-semibold text-gray-900'>
          {nextPoints != null ? formatPoints(nextPoints) : '--'}
        </div>
        <div className='text-sm text-gray-500'>
          {maxPoints != null ? formatPoints(maxPoints) : '--'}
        </div>
      </div>
    </div>
  );
};
